import { api } from "./api.js";

/**
 * Subscribing this browser to push notifications.
 *
 * Push covers the case the WebSocket cannot: the tab is closed, so there is no
 * socket. The service worker is woken by the browser's push service instead.
 *
 * Everything here is opt-in and reversible. Permission is only ever requested
 * from a deliberate action — asking on page load is how a prompt gets denied
 * permanently, and a denied permission cannot be re-requested without the
 * person digging through browser settings.
 */

export type PushState = "unsupported" | "unconfigured" | "denied" | "off" | "on";

/**
 * The VAPID key arrives base64url-encoded and the browser wants raw bytes.
 * Decoded here rather than on the server so the wire format stays readable.
 */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  const normalised = padded.replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalised);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  // Returned as the underlying buffer: `applicationServerKey` accepts a
  // BufferSource, and a typed array's generic buffer type does not always
  // satisfy that under the DOM lib's stricter definitions.
  return bytes.buffer;
}

/** Whether this browser can do push at all. Requires a secure context. */
export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/**
 * Registers the service worker.
 *
 * Idempotent — the browser returns the existing registration if there is one,
 * so this is safe to call on every load.
 */
async function register(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

/**
 * What state this browser is in, without prompting for anything.
 *
 * `unconfigured` means the deployment has no VAPID keys, so the control should
 * be hidden entirely rather than offering something the server cannot honour.
 */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";

  const { publicKey } = await api
    .get<{ publicKey: string | null }>("/push/public-key")
    .catch(() => ({ publicKey: null }));
  if (!publicKey) return "unconfigured";

  if (Notification.permission === "denied") return "denied";

  const registration = await register();
  if (!registration) return "unsupported";

  const existing = await registration.pushManager.getSubscription();
  return existing ? "on" : "off";
}

/**
 * Asks permission and registers this browser.
 *
 * Returns the resulting state so a caller can report what happened rather than
 * guessing: a refused prompt is a normal outcome, not an error.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";

  const { publicKey } = await api
    .get<{ publicKey: string | null }>("/push/public-key")
    .catch(() => ({ publicKey: null }));
  if (!publicKey) return "unconfigured";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "off";

  const registration = await register();
  if (!registration) return "unsupported";

  /*
   * `userVisibleOnly` is required by every browser that implements this: a
   * push must result in something the person can see. Silent pushes were used
   * for tracking, so they are not allowed on the web.
   */
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const json = subscription.toJSON();
  await api.post("/push/subscribe", {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });

  return "on";
}

/**
 * Stops push on this browser.
 *
 * Unsubscribes locally *and* tells the server, so a stale row does not sit
 * there being pushed to until the push service reports it dead.
 */
export async function disablePush(): Promise<PushState> {
  const registration = await register();
  if (!registration) return "unsupported";

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return "off";

  const { endpoint } = subscription;
  await subscription.unsubscribe().catch(() => undefined);
  await api.post("/push/unsubscribe", { endpoint }).catch(() => undefined);

  return "off";
}

/**
 * Take this browser's system notifications down once what they announced has
 * been read — otherwise they sit in the OS notification centre (Windows'
 * Action Center, macOS's Notification Center) long after the message was
 * answered. `tags` are the `resourceType:resourceId` tags the server pushes
 * with; null closes everything.
 *
 * Only reaches this browser, and only while a tab is open to run it; there is
 * no way to withdraw a notification from a browser that has no page open.
 */
export async function closeSystemNotifications(tags: string[] | null): Promise<void> {
  if (!pushSupported() || (tags !== null && tags.length === 0)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const shown = await registration.getNotifications();
    for (const notification of shown) {
      if (tags === null || tags.includes(notification.tag)) notification.close();
    }
  } catch {
    // Tidying up; a browser that refuses leaves the notification where it was.
  }
}
